import type { FieldValue, SecretsPatch } from './types';

/** The connection test, the Clarify catalog and fetch history. */
export const TEST_STATUSES = [
    'ok',
    'config_incomplete',
    'auth_failed',
    'unreachable',
    'no_data',
    'error',
] as const;
export type TestStatus = (typeof TEST_STATUSES)[number];

/** A failure cause the server names, so the page can say it without the provider's text. */
export const TEST_PEN_REASONS = [
    'stored_credential_undecryptable',
    'timed_out',
    'scada_item_unknown',
] as const;
export type TestPenReason = (typeof TEST_PEN_REASONS)[number];

export interface TestPenResult {
    cageId: number;
    status: TestStatus;
    daysWithData: number;
    signals: string[];
    message: string | null;
    reason: TestPenReason | null;
    /** The pen's SCADA items looked up in the Clarify account; null without SCADA items. */
    scada: TestScadaResult | null;
}

export interface TestScadaResult {
    itemCount: number;
    found: number;
    unknown: string[];
    /** Items a cut-off account list could not confirm either way. */
    unverified: number;
    /** No daily signal is mapped, so the lookup is the pen's whole test. */
    scadaOnly: boolean;
}

export interface TestResult {
    from: string;
    to: string;
    results: TestPenResult[];
    /** Null when no pen whose values the save needs signed was reached (R21). */
    testToken: string | null;
}

export interface TestPenInput {
    cageId: number;
    /** The pen's own values, per-pen connection overrides included (R16). */
    fields: Record<string, FieldValue>;
    secrets: SecretsPatch;
}

export interface TestProviderConfigInput {
    connection: Record<string, FieldValue>;
    secrets: SecretsPatch;
    pens: TestPenInput[];
}

export interface ClarifyCatalogInput {
    connection: Record<string, FieldValue>;
    secrets: SecretsPatch;
}

export interface ClarifyCatalogItem {
    id: string;
    name: string;
    labels: string[];
    engUnit: string | null;
}

export interface ClarifyCatalog {
    items: ClarifyCatalogItem[];
    truncated: boolean;
}

/** How Clarify answered a listing, classified as the connection test does. */
export const CATALOG_STATUSES = ['ok', 'auth_failed', 'unreachable', 'error'] as const;
export type ClarifyCatalogStatus = (typeof CATALOG_STATUSES)[number];
export const CATALOG_REASONS = ['timed_out', 'too_large', 'unreadable_answer'] as const;
export type ClarifyCatalogReason = (typeof CATALOG_REASONS)[number];

/** A listing's answer: the items when `status` is ok, none otherwise. */
export interface ClarifyCatalogAnswer extends ClarifyCatalog {
    status: ClarifyCatalogStatus;
    reason: ClarifyCatalogReason | null;
}

export interface FetchHistoryInput {
    cageId: number;
    /** YYYY-MM-DD, UTC. */
    from: string;
    to: string;
}

export interface SkippedProvider {
    providerName: string;
    reason: string;
}

export interface FetchHistoryResult {
    cageId: number;
    from: string;
    to: string;
    providerName: string | null;
    daysWithData: number;
    written: number;
    failedDays: string[];
    partialOnly: boolean;
    skipped: SkippedProvider[];
}
